import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as S from './InvoicePromotionBanner.styles';
import { InvoicePromotionDetails } from './InvoicePromotionDetails';
import type { InvoicePromotion } from '@/features/Profile/sections/Packages/api';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { ModalDrawer } from '@/components/UI/ModalDrawer';
import { RightChevronIcon } from '@/components/UI/Icons/Icons';
import { useInvoicePromotionPackages } from '@/features/Profile/sections/Packages/hooks';

type InvoicePromotionBannerProps = {
  promotion: InvoicePromotion;
};

export function InvoicePromotionBanner({
  promotion,
}: InvoicePromotionBannerProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isDetailsOpen, , openDetails, closeDetails] =
    useToggleOpenState(false);
  const { isPackagesLoading, promotionPackages } =
    useInvoicePromotionPackages(isDetailsOpen);

  const {
    title,
    shortDescription,
    textColor,
    iconUrl,
    bannerUrl,
    primaryColor,
    secondaryColor,
  } = promotion;
  const detailsTitle = title ?? shortDescription ?? '';

  const modalTitle = (
    <S.ModalTitle data-testid="invoice-promotion-title">
      {iconUrl && <img alt="" src={iconUrl} />}
      <S.ModalTitleText>{detailsTitle}</S.ModalTitleText>
    </S.ModalTitle>
  );

  return (
    <>
      <S.Banner
        $bannerUrl={bannerUrl}
        $primaryColor={primaryColor}
        $secondaryColor={secondaryColor}
        $textColor={textColor}
        data-testid="invoice-promotion-banner"
      >
        {iconUrl && <S.Icon alt="" src={iconUrl} role="presentation" />}
        <S.Content>
          {title && <S.Title>{title}</S.Title>}
          {shortDescription && (
            <S.Description>{shortDescription}</S.Description>
          )}
        </S.Content>
        <S.DetailsButton
          data-stat="notification-modal-open"
          type="button"
          onClick={openDetails}
        >
          <S.DetailsButtonText>{t('common.more_details')}</S.DetailsButtonText>
          <RightChevronIcon aria-hidden="true" />
        </S.DetailsButton>
      </S.Banner>

      <ModalDrawer
        background={theme.palette.primary.background}
        dataTestId="invoice-promotion-details"
        closeDataStat="notification-modal-close"
        footerPaddingBottom={50}
        isOpen={isDetailsOpen}
        maxWidth={900}
        onClose={closeDetails}
        title={modalTitle}
      >
        <InvoicePromotionDetails
          isPackagesLoading={isPackagesLoading}
          onNavigate={closeDetails}
          packages={promotionPackages}
          promotion={promotion}
        />
      </ModalDrawer>
    </>
  );
}
